/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IItemAddedToCartDialogData,
    IItemAddedToCartDialogResources,
    ILinesAddedToCartDialogResources,
    IOrderTemplateNameDialogResources,
    ItemAddedToCartDialog
} from '@msdyn365-commerce/components';
import MsDyn365, { AsyncResult, getCatalogId, getUrlSync, ICoreContext, IRequestContext } from '@msdyn365-commerce/core';
import { ICartActionResult } from '@msdyn365-commerce/global-state';
import { ProductList, ProductListLine } from '@msdyn365-commerce/retail-proxy';
import {
    deleteAsync,
    removeProductListLinesAsync,
    updateProductListLinesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';
import { BusinessPartnerUser, FeatureState, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    ArrayExtensions,
    createGetFeatureStateInput,
    getFeatureState,
    getPriceForSelectedVariant,
    getProductUrlSync,
    ObjectExtensions,
    OrderTemplate,
    OrderTemplateLine,
    PriceForSelectedVariantInput,
    StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Alert,
    getTelemetryObject,
    Heading,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    UncontrolledPagination
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { getSharedMessageComponent, getCreatedOrModifiedMessageComponent, IAuditMessageResources } from '../../common/auditMessages';

import {
    IOrderTemplateActionsProps,
    IOrderTemplateActionsViewProps,
    IOrderTemplateLineProps,
    IOrderTemplateLineViewProps,
    IOrderTemplateTableHeadingProps,
    OrderTemplateActionsView,
    OrderTemplateLinesStatusMessage,
    OrderTemplateLineView,
    OrderTemplateMobileLineView,
    OrderTemplateTableHeading
} from '../..';
import { ActiveOrderTemplateLinesInput } from './actions/get-order-template';
import { OrderTemplateBulkActions } from './components/bulk-actions';
import { IOrderTemplateData } from './order-template.data';
import { IOrderTemplateProps, IOrderTemplateResources } from './order-template.props.autogenerated';
import Spinner from './components/add-line/spinner';
import { IShareOrderTemplateDialogResources } from './components/order-template-share-modal';

export interface IOrderTemplateLineActionMessageState {
    itemKey: string;
    userMessage: string;
    statusCssString: string;
    messageGlyph: string;
    isOpen: boolean;
    isItemAddedToOrderTemplateOpen: boolean;
    isConfirmAddSelectionToBagModalOpen: boolean;
    itemAddedToCartDialogData?: IItemAddedToCartDialogData;
    productSelection: IOrderTemplateLineViewModel[];
    isDetailsDisplayed: boolean;
    detailedProductView?: IOrderTemplateLineViewProps;
    isQuantityDisabledForUpdate?: boolean;
}

export type orderTemplateLinesStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'EMPTY';
type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface IOrderTemplateViewProps extends IOrderTemplateProps<IOrderTemplateData> {
    OrderTemplateLines: IModuleProps;
    callbacks?: IOrderTemplateLinesCallbacks;
    status?: orderTemplateLinesStatus;
    statusMessage?: React.ReactNode;
    heading?: React.ReactNode;
    OrderTemplateActions?: INodeProps;
    ProductsTable?: INodeProps;
    ProductsTableRow?: INodeProps;
    ProductsTableHeading?: React.ReactNode;
    ProductDetails?: INodeProps;
    orderLines?: IOrderTemplateLineViewProps[];
    productListLines?: ProductListLine[];
    productsStatusMessage?: React.ReactNode;
    orderActions?: IOrderTemplateActionsViewProps | null;
    resources: IOrderTemplateResources;
    pagination?: React.ReactNode;
    itemAddedToCartDialog?: React.ReactNode;
    bulkOrderLineActions?: React.ReactNode;
    isDetailsDisplayed?: boolean;
    detailedProduct?: IOrderTemplateLineProps;
    detailedProductView?: IOrderTemplateLineViewProps;
    productQuantityView?: React.ReactNode;
    addButton?: React.ReactNode;
    isMobile?: boolean;
    b2bOrgOrderTemplateCreateShareInfo?: React.ReactNode;

    toggleDetailsDisplayed?(props: IOrderTemplateLineProps | undefined): void;
}

export interface IOrderTemplateLinesCallbacks {
    updateQuantity(itemKey: string, quantity: number): void;
}

export interface IOrderTemplateLineViewModel extends OrderTemplateLine {
    isChecked: boolean;
    productQuantityView: React.ReactNode;
}

/**
 *
 * OrderTemplateLines component.
 * @extends {React.PureComponent<IOrderTemplateProps<IOrderTemplateData>, IOrderTemplateLineActionMessageState>}
 */
@observer
class OrderTemplateLines extends React.Component<IOrderTemplateProps<IOrderTemplateData>, IOrderTemplateLineActionMessageState> {
    private readonly className: string;

    private readonly telemetryContent?: ITelemetryContent;

    @observable
    private _viewport: GridSize;

    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm';
    }

    constructor(props: IOrderTemplateProps<IOrderTemplateData>, state: IOrderTemplateLineActionMessageState) {
        super(props);
        this.className = 'ms-order-template';
        this._viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._updateViewport = this._updateViewport.bind(this);

        this.state = {
            itemKey: '',
            userMessage: '',
            statusCssString: 'success',
            messageGlyph: 'fas fa-check',
            isOpen: false,
            isItemAddedToOrderTemplateOpen: false,
            isConfirmAddSelectionToBagModalOpen: false,
            productSelection: [],
            isDetailsDisplayed: false,
            isQuantityDisabledForUpdate: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this._updateViewport();

        if (MsDyn365.isBrowser && !this.props.context.request.user.isAuthenticated && !this.props.context.request.params.isEditor) {
            window.location.assign(this.props.context.request.user.signInUrl || '/');
        }
    }

    @action
    public async componentDidMount(): Promise<void> {
        this.setState({});
        const orderTemplate = await this.props.data.orderTemplate;
        this.props.config.heading!.text = orderTemplate?.productList.Name || '';
        this.setState({
            productSelection: orderTemplate?.orderTemplateLines as IOrderTemplateLineViewModel[]
        });

        if (MsDyn365.isBrowser) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    public shouldComponentUpdate(
        nextProps: IOrderTemplateProps<IOrderTemplateData>,
        nextState: IOrderTemplateLineActionMessageState
    ): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { heading, className } = this.props.config;
        const { emptyOrderTemplateLinesText, errorGettingOrderTemplateLines } = this.props.resources;
        const { orderTemplate, cart, businessPartnerUser } = this.props.data;

        if (cart.status === 'LOADING' || ObjectExtensions.isNullOrUndefined(orderTemplate) || orderTemplate.status === 'LOADING') {
            const errorProps: IOrderTemplateViewProps = {
                ...(this.props as IOrderTemplateProps<IOrderTemplateData>),
                OrderTemplateLines: {
                    moduleProps: this.props,
                    className: classnames(this.className, className)
                },
                status: 'LOADING',
                statusMessage: <Spinner className={this.className} msg={this.props.resources.progressNotificationText} />,
                heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
                resources: this.props.resources
            };
            return this.props.renderView(errorProps);
        }

        if (cart.status === 'FAILED' || orderTemplate.status === 'FAILED') {
            const errorProps: IOrderTemplateViewProps = {
                ...(this.props as IOrderTemplateProps<IOrderTemplateData>),
                OrderTemplateLines: {
                    moduleProps: this.props,
                    className: classnames(`${this.className}`, className)
                },
                status: 'FAILED',
                statusMessage: <OrderTemplateLinesStatusMessage errorType='FAILED' text={errorGettingOrderTemplateLines} />,
                heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
                resources: this.props.resources
            };

            return this.props.renderView(errorProps);
        }

        const orderTemplateResult = orderTemplate && orderTemplate.result;
        const orderTemplateLines = orderTemplateResult && orderTemplateResult.orderTemplateLines;
        const businessPartnerUserResult = businessPartnerUser && businessPartnerUser.result;

        if (!orderTemplateResult || !orderTemplateLines || orderTemplateLines.length === 0) {
            if (orderTemplateResult && !this._isFirstPage() && MsDyn365.isBrowser) {
                window.location.href = this._getOrderTemplatePageUrl();
                return null;
            }

            const emptyViewProps: IOrderTemplateViewProps = {
                ...(this.props as IOrderTemplateProps<IOrderTemplateData>),
                OrderTemplateLines: {
                    moduleProps: this.props,
                    className: classnames(`${this.className}`, className)
                },
                status: 'EMPTY',
                statusMessage: <OrderTemplateLinesStatusMessage errorType='EMPTY' text={emptyOrderTemplateLinesText} />,
                heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
                resources: this.props.resources,
                orderActions:
                    orderTemplateResult &&
                    this._renderOrderTemplateActions(orderTemplateResult, this._isChannelMultipleCatalogsFeatureEnabled()),
                bulkOrderLineActions: orderTemplateResult && this._getOrderTemplateLineActions(orderTemplateResult.productList.Id),
                OrderTemplateActions: {
                    className: `${this.className}-action-bar`,
                    tag: 'div'
                }
            };

            return this.props.renderView(emptyViewProps);
        }

        const viewProps: IOrderTemplateViewProps = {
            ...(this.props as IOrderTemplateProps<IOrderTemplateData>),
            OrderTemplateLines: {
                moduleProps: this.props,
                className: classnames(`${this.className}`, className)
            },
            status: 'SUCCESS',
            productsStatusMessage: this.state.isOpen && (
                <div className={`${this.className}-products-status`}>
                    <Alert color={this.state.statusCssString} isOpen={this.state.isOpen} toggle={this._onDismiss}>
                        <span aria-live='assertive'>{this.state.userMessage}</span>
                    </Alert>
                </div>
            ),
            heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
            OrderTemplateActions: {
                className: `${this.className}-action-bar`,
                tag: 'div'
            },
            ProductsTable: {
                className: `${this.className}__table`,
                tag: 'table',
                role: 'grid'
            },
            ProductsTableRow: {
                className: `${this.className}-table-line`,
                tag: 'tr'
            },
            ProductsTableHeading: this._renderProductListHeader(this.props.resources),
            ProductDetails: {
                className: `${this.className}-table-line__link`,
                tag: 'a'
            },
            orderLines: this._renderProductList(),
            callbacks: { updateQuantity: debounce(this._changeQuantity, 2000) },
            orderActions: this._renderOrderTemplateActions(orderTemplateResult, this._isChannelMultipleCatalogsFeatureEnabled()),
            pagination: this._renderPagination(),
            itemAddedToCartDialog: this._renderItemAddedToCartDialog(),
            bulkOrderLineActions: this._getOrderTemplateLineActions(orderTemplateResult.productList.Id),
            isDetailsDisplayed: this.state.isDetailsDisplayed,
            toggleDetailsDisplayed: this._toggleDetailsDisplayed,
            detailedProductView: this.state.detailedProductView,
            isMobile: this.isMobile,
            b2bOrgOrderTemplateCreateShareInfo: this._getOrderTemplateCreateShareInfo(orderTemplateResult, businessPartnerUserResult)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public onSelect(line: IOrderTemplateLineViewModel, isChecked: boolean): void {
        line.isChecked = isChecked;

        const updated =
            this.state.productSelection.map(product => {
                if (product.fullProduct?.ProductDetails.ItemId === line.fullProduct?.ProductDetails.ItemId) {
                    line.isChecked = isChecked;
                }
                return product;
            }) || [];

        this.setState({
            productSelection: updated
        });
    }

    public onSelectAll(isChecked: boolean): void {
        const updated =
            this.state.productSelection.map(line => {
                line.isChecked = isChecked;
                return line;
            }) || [];

        this.setState({
            productSelection: updated
        });
    }

    private _renderProductListHeader(resources: IOrderTemplateResources): React.ReactNode {
        const {
            productImage,
            productNumberText,
            productText,
            productUnitPriceText,
            productUOMText,
            productQuantityText,
            productActionsText,
            selectAllRowsText,
            productLineCatalogName
        } = resources;
        const headers: IOrderTemplateTableHeadingProps = {
            productImage: productImage,
            productNumber: productNumberText,
            productName: productText,
            productUnitPrice: productUnitPriceText,
            productUOM: productUOMText,
            productQuantity: productQuantityText,
            productActions: productActionsText,
            selectAllRowsText,
            lines: this.state.productSelection,
            isMobile: this.isMobile,
            clickHandler: (isChecked: boolean) => {
                this.onSelectAll(isChecked);
            },
            isMultipleCatalogFeatureEnabledInHq: this._isChannelMultipleCatalogsFeatureEnabled(),
            productLineCatalogName: productLineCatalogName
        };

        return <OrderTemplateTableHeading {...headers} />;
    }

    private _getOrderTemplateLineActions(orderTemplateId: string): React.ReactNode {
        return (
            <OrderTemplateBulkActions
                {...this.props}
                orderTemplateId={orderTemplateId}
                selectedLines={this.state.productSelection && this.state.productSelection.filter(line => line.isChecked)}
                onAddSelectionToBag={this._onAddSelectionToBag}
                onRemoveSelection={this._onRemoveSelection}
                isMinifiedView={this.isMobile}
                isMultipleCatalogFeatureEnabledInHq={this._isChannelMultipleCatalogsFeatureEnabled()}
                telemetryContent={this.telemetryContent}
            />
        );
    }

    private _generateLinesAddedToCartDialogResources(): ILinesAddedToCartDialogResources {
        return {
            viewCartButtonText: this.props.resources.viewCartButtonText,
            orderTemplateCloseButtonText: this.props.resources.orderTemplateCloseButtonText,
            linesAddedToCartHeaderItemsOneText: this.props.resources.linesAddedToCartHeaderItemsOneText,
            linesAddedToCartHeaderLinesOneText: this.props.resources.linesAddedToCartHeaderLinesOneText,
            linesAddedToCartHeaderLinesFormatText: this.props.resources.linesAddedToCartHeaderLinesFormatText,
            linesAddedToCartHeaderMessageText: this.props.resources.linesAddedToCartHeaderMessageText
        };
    }

    private _generateShareOrderTemplateDialog(): IShareOrderTemplateDialogResources {
        return {
            shareOrderTemplateHeader: this.props.resources.shareOrderTemplateHeader,
            unShareOrderTemplateHeader: this.props.resources.unShareOrderTemplateHeader,
            shareOrderTemplatesDescription: this.props.resources.shareOrderTemplatesDescription,
            unshareOrderTemplatesDescription: this.props.resources.unshareOrderTemplatesDescription,
            shareOrderTemplateButtonText: this.props.resources.shareOrderTemplateButtonText,
            unshareOrderTemplateButtonText: this.props.resources.unshareOrderTemplateButtonText,
            cancelOrderTemplateShareButtonText: this.props.resources.cancelOrderTemplateShareButtonText
        };
    }

    private _generateItemAddedToCartDialogResources(): IItemAddedToCartDialogResources {
        return {
            viewCartButtonText: this.props.resources.viewCartButtonText,
            orderTemplateCloseButtonText: this.props.resources.orderTemplateCloseButtonText,
            itemAddedToCartHeaderItemOneText: this.props.resources.itemAddedToCartHeaderItemOneText,
            itemAddedToCartHeaderItemFormatText: this.props.resources.itemAddedToCartHeaderItemFormatText,
            itemAddedToCartHeaderMessageText: this.props.resources.itemAddedToCartHeaderMessageText,
            freePriceText: this.props.resources.freePriceText,
            originalPriceText: this.props.resources.originalPriceText,
            currentPriceText: this.props.resources.currentPriceText
        };
    }

    private readonly _isChannelMultipleCatalogsFeatureEnabled = (): boolean => {
        let isMultipleCatalogFeatureEnabledInHq = false;
        if (this.props.data.featureState) {
            isMultipleCatalogFeatureEnabledInHq =
                this.props.data.featureState.result?.find(
                    feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
                )?.IsEnabled || false;
        }
        return isMultipleCatalogFeatureEnabledInHq;
    };

    private readonly _onRemoveSelection = (): void => {
        const { context, resources } = this.props;
        const { telemetry } = context;
        const { removeSelectedFailureMessage, removeSelectedSuccessMessage } = resources;
        const lines = this.state.productSelection;
        const productListLines = lines && lines.length && lines.filter(line => line.isChecked).map(line => line.productListLine);

        if (productListLines && productListLines.length > 0) {
            const productListId = productListLines[0].ProductListId;

            const maxWaitTimeInMilliSeconds: number = 1000;
            removeProductListLinesAsync({ callerContext: context.actionContext }, productListId!, productListLines)
                .then(async () => {
                    this.setState({
                        userMessage: removeSelectedSuccessMessage,
                        statusCssString: 'success',
                        messageGlyph: 'fas fa-check',
                        isOpen: true
                    });
                    // eslint-disable-next-line no-promise-executor-return -- fix
                    await new Promise(resolve => setTimeout(resolve, maxWaitTimeInMilliSeconds));
                    window.location.reload();
                })
                .catch(error => {
                    telemetry.warning('[order-template-lines] Unable remove items from the order template lines');
                    telemetry.error(error);
                    this.setState({
                        userMessage: removeSelectedFailureMessage,
                        statusCssString: 'success',
                        messageGlyph: 'fas fa-check',
                        isOpen: true
                    });
                });
        }
    };

    private readonly _getOrderTemplatePageUrl = (): string => {
        let url = getUrlSync('ordertemplate', this.props.context.actionContext) || '';
        url = url.includes('?') ? url.substr(0, url.indexOf('?')) : url;
        if (this.props.data.orderTemplate?.result?.productList.Id) {
            return `${url}?id=${this.props.data.orderTemplate.result.productList.Id}`;
        }
        return '';
    };

    private readonly _onAddSelectionToBag = async (): Promise<void> => {
        const lines = this.state.productSelection;
        const selectedLines = lines.filter(line => line.isChecked);
        if (selectedLines.length > 0) {
            for (const productLine of selectedLines) {
                const itemKey = productLine.fullProduct?.ProductDetails.ItemId ?? '';
                const product = productLine.fullProduct?.ProductDetails ?? {
                    RecordId: 0,
                    ProductTypeValue: 0,
                    BasePrice: 0,
                    Price: 0,
                    AdjustedPrice: 0
                };
                const quantity = productLine.productListLine.Quantity ?? 0;
                if (this._isChannelMultipleCatalogsFeatureEnabled()) {
                    const catalogId = productLine.productListLine.CatalogId;
                    await this._addItemToBag(product, quantity, itemKey, catalogId);
                } else {
                    await this._addItemToBag(product, quantity, itemKey);
                }
            }
        }
        return Promise.resolve(void 0);
    };

    private _renderOrderTemplateActions(
        orderTemplate: OrderTemplate,
        isMultipleCatalogFeatureEnabledInHq: boolean
    ): IOrderTemplateActionsViewProps {
        const { id, typeName, context, resources, data } = this.props;
        const {
            addTemplateToBagButtonText,
            renameTemplateButtonText,
            templateShareButtonText,
            templateUnshareButtonText,
            deleteTemplateButtonText,
            closeWindowButtonText,
            addToCartFailureMessage,
            addToCartSuccessMessage,
            addToCartProcessMessage,
            orderTemplateErrorMessage
        } = resources;
        const isB2bAdmin = data.businessPartnerUser?.result?.IsAdmin ?? false;

        const orderTemplateActions: IOrderTemplateActionsProps = {
            data: { orderTemplate },
            id,
            typeName,
            context,
            telemetryContent: this.telemetryContent,
            handlers: {
                onRenameTemplate: this._renameTemplate,
                onShareTemplate: this._toggleIsShared,
                onRemoveTemplate: this._removeTemplate
            },
            isMultipleCatalogFeatureEnabledInHq,
            isShared: orderTemplate.productList?.IsShared ?? false,
            isB2bAdmin: isB2bAdmin,
            isB2bContact: (this.props.context.request.user as { isB2bContact?: boolean }).isB2bContact === true,
            showOptionsLabel: true,
            renameOrderTemplateDialogStrings: this._generateOrderTemplateNameDialogResources(resources),
            resources: {
                addTemplateToBagButtonText,
                renameTemplateButtonText,
                templateShareButtonText,
                templateUnshareButtonText,
                deleteTemplateButtonText,
                addToCartFailureMessage,
                addToCartSuccessMessage,
                addToCartProcessMessage,
                closeWindowButtonText,
                orderTemplateErrorMessage,
                linesAddedToCartDialogResources: this._generateLinesAddedToCartDialogResources(),
                ShareOrderTemplateDialog: this._generateShareOrderTemplateDialog()
            }
        };

        return OrderTemplateActionsView(orderTemplateActions);
    }

    private _generateOrderTemplateNameDialogResources(resources: IOrderTemplateResources): IOrderTemplateNameDialogResources {
        return {
            orderTemplateHeaderLabel: resources.renameOrderTemplateTitleText,
            orderTemplateTitleLabel: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateTitle,
            orderTemplateDescription: '',
            defaultOrderTemplateName: resources.defaultOrderTemplateName,
            orderTemplateButtonLabel: resources.renameOrderTemplateButtonText,
            orderTemplateCancelButtonLabel: resources.cancelBtnLabel
        };
    }

    private readonly _getCurrentUrl = (reqContext: IRequestContext): URL => {
        if (MsDyn365.isBrowser) {
            return new URL(window.location.href);
        }
        return new URL(reqContext.url.requestUrl.href);
    };

    /**
     * On close handler.
     */
    private readonly onCloseHandler = () => {
        this.setState({
            itemKey: this.state.itemKey,
            userMessage: this.state.userMessage,
            statusCssString: this.state.statusCssString,
            messageGlyph: this.state.messageGlyph,
            isOpen: this.state.isOpen,
            isItemAddedToOrderTemplateOpen: false
        });
    };

    private readonly _renderItemAddedToCartDialog = (): React.ReactNode | null => {
        if (!this.state.itemAddedToCartDialogData) {
            return null;
        }
        const isMultipleCatalogFeatureEnabledInHq =
            this.props.data.featureState?.result?.find(feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature')
                ?.IsEnabled || false;

        return (
            <ItemAddedToCartDialog
                className='msc-item-added-to-cart-dialog'
                id={this.props.id}
                typeName={this.props.typeName}
                context={this.props.context}
                dialogStrings={this._generateItemAddedToCartDialogResources()}
                data={this.state.itemAddedToCartDialogData}
                modalOpen={this.state.isItemAddedToOrderTemplateOpen}
                onClose={this.onCloseHandler}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
            />
        );
    };

    private _isFirstPage(): boolean {
        return this._getCurrentPageNumber() === 0;
    }

    private _getCurrentPageNumber(): number {
        const itemsPerPage = this.props.config.itemsPerPage || 50;
        return (this.props.context.request.query && +this.props.context.request.query.skip / itemsPerPage) || 0;
    }

    private readonly _renderPagination = (): React.ReactNode | null => {
        const { config, context, data, resources } = this.props;
        const orderTemplate = data && data.orderTemplate && data.orderTemplate.result;
        if (!orderTemplate) {
            return null;
        }

        const fullUrl = this._getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 50;
        const currentPageNumber = this._getCurrentPageNumber();
        const skipCount = currentPageNumber * itemsPerPage;
        const totalItems = orderTemplate && orderTemplate.totalLines;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }

        return (
            <UncontrolledPagination
                className={`${this.className}__pagination`}
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
            />
        );
    };

    private _renderProductList(): IOrderTemplateLineViewProps[] {
        const {
            selectOneRowText,
            moreButtonText,
            viewProductDetailsText,
            priceFree,
            originalPriceText,
            currentPriceText,
            addOrderTemplateItemToBagButtonText,
            removeItemFromOrderTemplateButtonText,
            productDimensionTypeColor,
            productDimensionTypeSize,
            productDimensionTypeStyle,
            inputQuantityAriaLabel,
            deleteButtonText,
            incrementButtonAriaLabel,
            decrementButtonAriaLabel,
            unavailableProductErrorMessage,
            outOfStockProductErrorMessage
        } = this.props.resources;

        const { context, telemetry, data } = this.props;
        const { catalogs } = data;
        const { imageSettings } = this.props.config;

        return this.state.productSelection.map((line, index) => {
            const currentProductProps: IOrderTemplateLineProps = {
                orderTemplateLine: line,
                productListLine: line.productListLine,
                price: line.fullProduct?.ProductPrice,
                error: line.error,
                shouldDisable: line.shouldDisable,
                context,
                isQuantityDisabledForUpdate: this.state.isQuantityDisabledForUpdate,
                productUrl: line.fullProduct
                    ? this._updateProductUrlCatalog(line.fullProduct.ProductDetails, context, line.productListLine.CatalogId)
                    : '',
                imageSettings,
                telemetry,
                moduleId: this.props.id,
                moduleTypeName: this.props.typeName,
                quantity: line.productListLine.Quantity!,
                handlers: {
                    onAddToBag: this._addItemToBag,
                    onRemoveItem: this._removeItemFromOrderTemplateLines,
                    onChangeQuantity: this._changeQuantity,
                    onSelect: this.onSelect.bind(this)
                },
                resources: {
                    selectOneRowText,
                    moreButtonText,
                    viewProductDetailsText,
                    productDimensionTypeColor,
                    productDimensionTypeSize,
                    productDimensionTypeStyle,
                    freePriceText: priceFree,
                    originalPriceText,
                    currentPriceText,
                    inputQuantityAriaLabel,
                    removeFromOrderTemplateLinesText: removeItemFromOrderTemplateButtonText,
                    addToBagButtonText: addOrderTemplateItemToBagButtonText,
                    deleteButtonText,
                    incrementButtonAriaLabel,
                    decrementButtonAriaLabel,
                    unavailableProductErrorMessage,
                    outOfStockProductErrorMessage
                },
                index,
                isMobile: this.isMobile,
                toggleDetailsDisplayed: this._toggleDetailsDisplayed,
                isMultipleCatalogFeatureEnabledInHq: this._isChannelMultipleCatalogsFeatureEnabled(),
                catalogs: catalogs && catalogs.result
            };

            return OrderTemplateLineView(currentProductProps)!;
        });
    }

    private readonly _toggleDetailsDisplayed = (props: IOrderTemplateLineProps): void => {
        let state = {
            isDetailsDisplayed: !this.state.isDetailsDisplayed
        };
        if (props) {
            state = {
                ...state,
                ...{
                    detailedProductView: OrderTemplateMobileLineView(props)
                }
            };
        }
        this.setState(state);
    };

    private readonly _getOrderTemplateCreateShareInfo = (
        orderTemplate: OrderTemplate,
        businessPartnerUser: BusinessPartnerUser | undefined
    ): React.ReactNode => {
        const {
            orderTemplateCreatedOnText,
            orderTemplateSharedOnText,
            orderTemplateModifiedOnText,
            orderTemplateYouText,
            orderTemplateForText,
            orderTemplateByText
        } = this.props.resources;

        const auditMessageResources: IAuditMessageResources = {
            orderTemplateCreatedText: orderTemplateCreatedOnText,
            orderTemplateSharedText: orderTemplateSharedOnText,
            orderTemplateModifiedText: orderTemplateModifiedOnText,
            orderTemplateYouText,
            orderTemplateForText,
            orderTemplateByText
        };

        return (
            <div className='organization-order-template-shared-status'>
                {getSharedMessageComponent(orderTemplate, businessPartnerUser, auditMessageResources, this._formatOperationDate)}
                {getCreatedOrModifiedMessageComponent(orderTemplate, businessPartnerUser, auditMessageResources, this._formatOperationDate)}
            </div>
        );
    };

    private readonly _formatOperationDate = (operationDate: Date): string => {
        return operationDate.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // This method is used to update the product url with the catalogId
    private readonly _updateProductUrlCatalog = (
        productDetails: SimpleProduct,
        context: ICoreContext,
        catalogId: number | undefined
    ): string => {
        const productUrl = getProductUrlSync(productDetails, context.actionContext, undefined);
        if (!StringExtensions.isNullOrWhitespace(productUrl) && catalogId !== undefined) {
            const absoluteUrl = new URL(productUrl, window.location.href);
            absoluteUrl.searchParams.set('catalogid', `${catalogId ?? 0}`);
            return absoluteUrl.href;
        }

        return productUrl;
    };

    // Dismiss alert when addingToCart/removeItem
    private readonly _onDismiss = () => {
        this.setState({
            isOpen: false
        });
    };

    private readonly _changeQuantity = async (itemKey: string, quantity: number, props?: IOrderTemplateLineProps) => {
        const { context, data, telemetry, config, resources } = this.props;
        const { orderTemplate } = data;

        if (context) {
            if (orderTemplate?.result) {
                const orderTemplateLinesData = orderTemplate.result.orderTemplateLines;
                if (orderTemplateLinesData) {
                    const orderLineIndex = orderTemplateLinesData.findIndex(
                        (lines: OrderTemplateLine) => lines.productListLine.LineId === itemKey
                    );
                    const newProductListLine = { ...orderTemplateLinesData[orderLineIndex] };
                    if (orderTemplateLinesData[orderLineIndex].productListLine.Quantity === quantity) {
                        return;
                    }
                    const prevQuantity = newProductListLine.productListLine.Quantity;
                    newProductListLine.productListLine.Quantity = quantity;
                    const { fullProduct, ...line } = newProductListLine;

                    // Update mobile modal
                    if (props) {
                        props.quantity = quantity;
                        this.setState({
                            detailedProductView: OrderTemplateMobileLineView(props)
                        });
                    }

                    this.setState({
                        isQuantityDisabledForUpdate: true
                    });

                    updateProductListLinesAsync({ callerContext: context.actionContext }, orderTemplate.result.productList.Id, [
                        line.productListLine
                    ])
                        .then(list => {
                            this.setState({
                                itemKey,
                                userMessage: '',
                                statusCssString: 'success',
                                messageGlyph: 'fas fa-check',
                                isQuantityDisabledForUpdate: false,
                                isOpen: false
                            });
                            if (list) {
                                const newLine = list.find(lines => lines.LineId === itemKey);
                                orderTemplateLinesData[orderLineIndex].productListLine = newLine!;
                                const updatedLines = [...orderTemplateLinesData];
                                updatedLines[orderLineIndex] = { fullProduct, productListLine: newLine! };
                                const updatedOrderTemplate = { ...orderTemplate.result, ProductListLines: updatedLines };
                                context.actionContext.update(
                                    new ActiveOrderTemplateLinesInput(context.actionContext.requestContext, config),
                                    updatedOrderTemplate
                                );
                            }
                        })
                        .catch(error => {
                            telemetry.warning('[order-template-lines] Unable to update product quantity.');
                            telemetry.error(error);
                            const userMessage = resources.editOrderTemplateLineQuantityError;
                            if (props) {
                                props && (props.productListLine.Quantity = prevQuantity);
                                this.setState({
                                    itemKey,
                                    userMessage,
                                    statusCssString: 'danger',
                                    messageGlyph: 'fas fa-exclamation-triangle',
                                    isQuantityDisabledForUpdate: false,
                                    isOpen: true,
                                    detailedProductView: OrderTemplateMobileLineView(props)
                                });
                            } else {
                                newProductListLine.productListLine.Quantity = prevQuantity;
                                this.setState({
                                    itemKey,
                                    userMessage,
                                    statusCssString: 'danger',
                                    messageGlyph: 'fas fa-exclamation-triangle',
                                    isQuantityDisabledForUpdate: false,
                                    isOpen: true
                                });
                            }
                        });
                }
            }
        }
    };

    private readonly _addItemToBag = async (
        product: SimpleProduct,
        quantity: number,
        itemKey: string,
        catalogId?: number | undefined
    ): Promise<void> => {
        const { context, data, resources, telemetry } = this.props;
        const { cart } = data;
        const { maxQuantityLimitText, addedToCartFailureMessage } = resources;

        if (context) {
            if (cart.result) {
                const addProductToCartInput = {
                    product,
                    count: quantity,
                    catalogId
                };

                await cart.result
                    .addProductToCart(addProductToCartInput)
                    .then((result: ICartActionResult) => {
                        if (result.status === 'SUCCESS') {
                            return getPriceForSelectedVariant(
                                new PriceForSelectedVariantInput(
                                    product.RecordId,
                                    this.props.context.actionContext.requestContext.apiSettings.channelId,
                                    product
                                ),
                                this.props.context.actionContext
                            ).then(priceResult => {
                                if (!priceResult) {
                                    return AsyncResult.reject('Failed to retrieve product price');
                                }
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- read config file.
                                const isMiniCart = context.app.config.addToCartBehavior === 'showMiniCart';
                                this.setState({
                                    itemKey,
                                    userMessage: '',
                                    statusCssString: 'success',
                                    messageGlyph: 'fas fa-check',
                                    isOpen: false,
                                    isItemAddedToOrderTemplateOpen: !isMiniCart,
                                    itemAddedToCartDialogData: {
                                        product,
                                        productPrice: priceResult,
                                        quantity
                                    },
                                    isDetailsDisplayed: false
                                });
                                return AsyncResult.resolve();
                            });
                        } else {
                            switch (result.substatus) {
                                case 'MAXQUANTITY':
                                    if (context.app.config.maxQuantityForCartLineItem) {
                                        const cartLineQuantity: number | undefined =
                                            cart.result?.cart.CartLines &&
                                            cart.result?.cart.CartLines.find(result => result.ProductId === product.RecordId)?.Quantity;
                                        const defaultMessage = `You can only add ${context.app.config.maxQuantityForCartLineItem} of this item to your shopping bag`;
                                        this.setState({
                                            itemKey,
                                            userMessage:
                                                cartLineQuantity !== undefined
                                                    ? maxQuantityLimitText
                                                          .replace('{curQuantity}', cartLineQuantity.toString())
                                                          .replace(
                                                              '{maxQuantity}',
                                                              context.app.config.maxQuantityForCartLineItem.toString()
                                                          ) || defaultMessage
                                                    : defaultMessage,
                                            statusCssString: 'danger',
                                            messageGlyph: 'fas fa-exclamation-triangle',
                                            isOpen: true
                                        });
                                        return AsyncResult.resolve();
                                    } else {
                                        const defaultMessage = `Max quantity for cart line item is undefined. Please define in configuration settings.`;
                                        this.setState({
                                            itemKey,
                                            userMessage: defaultMessage,
                                            statusCssString: 'danger',
                                            messageGlyph: 'fas fa-exclamation-triangle',
                                            isOpen: true
                                        });
                                        return AsyncResult.resolve();
                                    }
                                case 'INVOICEINCART': {
                                    this.setState({
                                        itemKey,
                                        userMessage: result.errorDetails?.LocalizedMessage || addedToCartFailureMessage,
                                        statusCssString: 'danger',
                                        messageGlyph: 'fas fa-exclamation-triangle',
                                        isOpen: true
                                    });
                                    return AsyncResult.resolve();
                                }
                                default:
                                    telemetry.debug('[order-template-lines] Unable to Update Cart Line from the order template lines');
                                    this.setState({
                                        itemKey,
                                        userMessage: addedToCartFailureMessage,
                                        statusCssString: 'danger',
                                        messageGlyph: 'fas fa-exclamation-triangle',
                                        isOpen: true
                                    });
                                    return AsyncResult.resolve();
                            }
                        }
                    })
                    .catch((error: Error) => {
                        telemetry.warning(error.message);
                        telemetry.debug('[order-template-lines] Error trying to Update Cart Line from the order template lines');
                        if (StringExtensions.isNullOrWhitespace(error.message)) {
                            this.setState({
                                itemKey,
                                userMessage: error.message,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            });
                        } else {
                            this.setState({
                                itemKey,
                                userMessage: addedToCartFailureMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            });
                        }
                    });
            }
        }
    };

    private readonly _removeItemFromOrderTemplateLines = async (line: IOrderTemplateLineViewModel) => {
        const { context, data, telemetry } = this.props;
        const { orderTemplate } = data;

        if (context && context.actionContext) {
            const maxWaitTimeInMilliSeconds: number = 1000;
            const userToken = context.request.user.isAuthenticated && context.request.user.token;
            if (userToken && orderTemplate?.result && line) {
                const featureStates = await getFeatureState(createGetFeatureStateInput(context.actionContext), context.actionContext);
                let isMultipleCatalogFeatureEnabledInHq = false;
                if (ArrayExtensions.hasElements(featureStates)) {
                    isMultipleCatalogFeatureEnabledInHq =
                        featureStates.find(
                            (featureState: FeatureState) => featureState.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
                        )?.IsEnabled || false;
                }
                const catalogId = getCatalogId(context.actionContext.requestContext);
                if (isMultipleCatalogFeatureEnabledInHq) {
                    line.productListLine.CatalogId = catalogId;
                }
                removeProductListLinesAsync({ callerContext: context.actionContext }, line.productListLine.ProductListId!, [
                    line.productListLine
                ])
                    .then(async () => {
                        // eslint-disable-next-line no-promise-executor-return -- fix
                        await new Promise(resolve => setTimeout(resolve, maxWaitTimeInMilliSeconds));
                        window.location.reload();
                    })
                    .catch(error => {
                        telemetry.warning('[order-template-lines] Unable remove items from the order template lines');
                        telemetry.error(error);
                    });
            } else {
                telemetry.warning(
                    '[order-template-lines] Unable remove items from the order template lines user token or lineItemID was not found'
                );
            }
        }
    };

    private readonly _renameTemplate = async (template: ProductList): Promise<void> => {
        try {
            const orderTemplate = await this.props.data.orderTemplate;
            if (orderTemplate) {
                const updatedOrderTemplate = { ...orderTemplate, productList: template };
                this.props.context.actionContext.update(
                    new ActiveOrderTemplateLinesInput(this.props.context.actionContext.requestContext, this.props.config),
                    updatedOrderTemplate
                );
            }
        } catch (error) {
            this.props.context.telemetry.warning('[order-template] Unable to refresh order template after rename.');
            this.props.context.telemetry.error(error as string);
        }
        return Promise.resolve();
    };

    private readonly _removeTemplate = () => {
        const { context, data, telemetry, resources } = this.props;
        const { orderTemplate } = data;

        if (context && context.actionContext) {
            const userToken = context.request.user.isAuthenticated && context.request.user.token;
            if (userToken && orderTemplate && orderTemplate.result) {
                deleteAsync({ callerContext: context.actionContext }, orderTemplate.result.productList)
                    .then()
                    .catch((error: string) => {
                        telemetry.warning('[order-template] Unable to delete order template.');
                        telemetry.error(error);
                        this.setState({
                            itemKey: orderTemplate.result?.productList.Id || '',
                            userMessage: resources.deleteOrderTemplateFailedText,
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: true
                        });
                    });
                const newLink = getUrlSync('ordertemplatelist', this.props.context.actionContext);
                if (newLink) {
                    window.location.assign(newLink);
                }
            } else {
                telemetry.warning('[order-template] Failed to delete order template. Order template was not found');
            }
        } else {
            telemetry.warning('[order-template] Failed to delete order template.');
        }
    };

    private _updateViewport(): void {
        this._viewport = this._getViewport();
    }

    private readonly _getViewport = (): GridSize => {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'xs';
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className={`${this.props.config.className}__pagination-left`} aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className={`${this.props.config.className}__pagination-right`} aria-hidden='true' />
            </div>
        );
    };

    private _toggleIsShared = async (updatedProductList: ProductList): Promise<void> => {
        try {
            const orderTemplate = await this.props.data.orderTemplate;
            if (orderTemplate) {
                const updatedOrderTemplate = { ...orderTemplate, productList: updatedProductList };
                this.props.context.actionContext.update(
                    new ActiveOrderTemplateLinesInput(this.props.context.actionContext.requestContext, this.props.config),
                    updatedOrderTemplate
                );
            }
        } catch (error) {
            this.props.context.telemetry.warning('[order-template] Unable to update order template sharing state.');
            this.props.context.telemetry.error(error as string);
        }
    };
}

export default OrderTemplateLines;
